import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { t as uiText, type UiLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { commitUiLanguage, saveUiLanguage } from '../i18n/commitUiLanguage';
import { languageFromBrowser, noteExplicitUiLanguageChoice, uiLanguageRevision } from '../i18n/uiLanguage';

const OPTIONS: { value: UiLanguage; label: string }[] = [
  { value: 'ja', label: '日本語' },
  { value: 'en', label: 'English' },
];

export function LanguageSwitcher({ variant = 'header' }: { variant?: 'header' | 'card' }) {
  const onCard = variant === 'card';
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const active = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
  const current: UiLanguage = active.startsWith('ja') ? 'ja' : 'en';
  const currentLabel = OPTIONS.find((option) => option.value === current)?.label ?? 'English';

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const select = (next: UiLanguage) => {
    setOpen(false);
    if (next === current) return;
    noteExplicitUiLanguageChoice();
    commitUiLanguage(next);
    if (!user) return;
    void saveUiLanguage(user.uid, next).catch((error) => {
      console.warn('[LanguageSwitcher] uiLanguage の保存に失敗しました', error);
    });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={uiText('Display language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: '30px',
          padding: '0 8px 0 10px',
          borderRadius: '999px',
          border: onCard ? '1px solid #e4d5c6' : '1px solid rgba(255, 255, 255, 0.55)',
          background: onCard
            ? open
              ? '#f3ebe3'
              : '#fff'
            : open
              ? 'rgba(255, 255, 255, 0.24)'
              : 'rgba(255, 255, 255, 0.14)',
          color: onCard ? '#5c4632' : 'white',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          lineHeight: 1,
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(event) => {
          if (!open) event.currentTarget.style.background = onCard ? '#f6f1eb' : 'rgba(255, 255, 255, 0.22)';
        }}
        onMouseLeave={(event) => {
          if (!open) event.currentTarget.style.background = onCard ? '#fff' : 'rgba(255, 255, 255, 0.14)';
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.7" />
          <path d="M3.75 12h16.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path
            d="M12 3.75c2.15 2.35 3.25 5.15 3.25 8.25s-1.1 5.9-3.25 8.25c-2.15-2.35-3.25-5.15-3.25-8.25s1.1-5.9 3.25-8.25z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        </svg>
        <span>{currentLabel}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          style={{
            opacity: 0.9,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.18s ease',
          }}
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={uiText('Display language')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '148px',
            padding: '6px',
            backgroundColor: '#fff',
            border: '1px solid #eee',
            borderRadius: '12px',
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.18)',
            zIndex: 10020,
          }}
        >
          {OPTIONS.map((option) => {
            const selected = option.value === current;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => select(option.value)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  background: selected ? 'var(--workspace-bg-soft, #E9D8C9)' : 'transparent',
                  color: selected ? 'var(--workspace-primary-dark, #8b6f47)' : '#3d342c',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: selected ? 700 : 500,
                  textAlign: 'left',
                }}
                onMouseEnter={(event) => {
                  if (!selected) event.currentTarget.style.background = '#f6f1eb';
                }}
                onMouseLeave={(event) => {
                  if (!selected) event.currentTarget.style.background = 'transparent';
                }}
              >
                <span>{option.label}</span>
                {selected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M5 12.5l4.2 4.2L19 7.5"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** ログイン前の画面。未ログインならブラウザ言語、対応が無ければ英語。DB には書かない。 */
export function AuthLanguageSwitcher() {
  const { user, initializing } = useAuth();

  useEffect(() => {
    if (initializing || user) return;
    if (uiLanguageRevision() > 0) return;
    commitUiLanguage(languageFromBrowser(), { cache: false });
  }, [initializing, user]);

  return (
    <div className="auth-language-switcher">
      <LanguageSwitcher variant="card" />
    </div>
  );
}
